"use client";

import { useState, type FormEvent } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { defaultLoginPath, safeNextPath } from "@/lib/auth/safe-next";
import { Button } from "@/components/ui/Button";
import { inputClass, labelClass } from "@/components/ui/Field";

const ERROR_MESSAGES: Record<string, string> = {
  invalid_credentials: "Invalid email/username or password.",
  invalid_body: "Invalid email/username or password.",
  account_disabled: "This account has been disabled. Contact an administrator.",
  no_roles: "This account has no assigned roles. Contact an administrator.",
  unit_account:
    "This is a CareStation unit account. Units sign in on the kiosk itself, not here.",
  rate_limited: "Too many login attempts. Please wait a few minutes and try again.",
};

function messageFor(code: string | undefined): string {
  if (!code) return "Invalid email/username or password.";
  return ERROR_MESSAGES[code] ?? "Invalid email/username or password.";
}

export function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch("/api/v1/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (res.ok) {
        const body = (await res.json()) as { roles?: { role?: unknown }[] };
        const roles = Array.isArray(body.roles)
          ? body.roles.flatMap((grant) =>
              typeof grant.role === "string" ? [grant.role] : [],
            )
          : [];
        router.push(
          safeNextPath(searchParams.get("next"), defaultLoginPath(roles)),
        );
        return;
      }
      let code: string | undefined;
      try {
        const body = (await res.json()) as { error?: string };
        code = body?.error;
      } catch {
        code = undefined;
      }
      setError(messageFor(code));
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="w-full space-y-4" noValidate>
      <div>
        <label htmlFor="email" className={labelClass}>
          Email or username
        </label>
        <input
          id="email"
          name="email"
          type="text"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          className={inputClass}
        />
      </div>
      <div>
        <label htmlFor="password" className={labelClass}>
          Password
        </label>
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          className={inputClass}
        />
      </div>
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
      <Button type="submit" disabled={submitting} className="w-full">
        Sign in
      </Button>
    </form>
  );
}
